// useFullscreen — 全画面 (Fullscreen API) の共有フック (2026-06-12 抽出).
//
// 由来: もともと `ClassicLayout.tsx` 内 inline で持っていた fullscreen state
// (isFullscreen / fullscreenSupported) + handleToggleFullscreen + fullscreenchange
// listener を、Classic / Modern 共通のヘッダ (RoomHeader) で同じ「全画面」項目を
// 出すために共有フックへ切り出した。挙動は Classic 原典そのまま (= 不変)。
//
// 2026-05-15 Classic 原典コメント:
//   全画面 toggle (Fullscreen API). document.fullscreenElement を真値判定して
//   enter/exit を切替. fullscreenchange イベントで OS UI / ESC 経由の解除も React
//   state に反映する. SSR / sandbox iframe 等で API 不在 (= requestFullscreen
//   undefined) のケースは `supported=false` を返し、呼び出し側はボタン自体を出さない.

import { useCallback, useEffect, useState } from 'react';

export interface UseFullscreenResult {
  /** 現在 document が全画面か (fullscreenchange に追従)。 */
  readonly isFullscreen: boolean;
  /** Fullscreen API が利用可能か (= 不可なら呼び出し側はボタンを出さない)。 */
  readonly supported: boolean;
  /** 全画面の enter / exit をトグルする。 */
  readonly toggle: () => void;
}

export const useFullscreen = (): UseFullscreenResult => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    typeof document !== 'undefined' && document.fullscreenElement !== null,
  );
  const supported =
    typeof document !== 'undefined' &&
    typeof document.documentElement.requestFullscreen === 'function';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = (): void => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
    };
  }, []);

  const toggle = useCallback((): void => {
    if (typeof document === 'undefined') return;
    if (document.fullscreenElement !== null) {
      void document.exitFullscreen?.();
    } else {
      void document.documentElement.requestFullscreen?.();
    }
  }, []);

  return { isFullscreen, supported, toggle };
};
